import { z } from 'zod';
import { Priority } from '../types/task';

// ─── View Mode ───

/**
 * Validation for view mode selection.
 * Used in GET /api/v1/tasks?view=... to select between different views.
 */
export const ViewModeEnum = z.enum(['board', 'table', 'list']);

// ─── Sortable Fields ───

/**
 * Validation for sortable fields in table view.
 * Constraints to known Task fields to prevent column injection.
 */
// ─── Columnas de tarea configurables (GHE #1099) ───
//
// El catálogo de campos que una vista puede pintar como columna. Vive AQUÍ, junto a
// `TableSortFieldEnum`, porque es el mismo vocabulario: los nombres coinciden con los que ya
// usan la ordenación y `TableHeader.tsx`, para que no haya una capa de traducción entre lo que
// se guarda, lo que se ordena y lo que se pinta.
//
// 🚨 `checkbox` NO está aquí a propósito. Es cromo de la tabla, no un campo de la tarea: aparece
// o no según el permiso de escritura (`getVisibleColumns`), y ofrecerlo como columna elegible
// dejaría al usuario apagar su propia selección múltiple.
//
// Lo que NO se ofrece, y por qué (decisión de diseño, mockup `docs/design/custom-view-columns-1099.html`):
//   · `description` — texto largo: reventaría la altura de fila, o se cortaría hasta ser inútil.
//   · ids (`listId`, `statusId`, `clientId`, `taskTypeId`, `parentId`) — ya representados por su nombre.
//   · `position`, `version` — maquinaria interna, no información de la tarea.
//   · campos personalizados por lista — fuera de alcance (dueño, 2026-09-08): harían que el
//     catálogo dependiera de la lista, y una vista podría citar columnas que en otra no existen.
export const TaskColumnEnum = z.enum([
  'title',
  'status',
  'priority',
  'assignee',
  'client',
  'dueDate',
  'startDate',
  'estimate',
  'actualMinutes',
  'taskType',
  'isMilestone',
  'completedAt',
  'deadlineMissedAt',
  'requiresValidation',
  'relatedTask',
  'previousDeveloper',
  'externalUrl',
  'dataverseLink',
  'createdAt',
  'updatedAt',
]);

/**
 * La columna que no se puede quitar. Sin el título la fila deja de identificar nada: quedaría
 * una tabla de estados y fechas sin sujeto.
 */
export const MANDATORY_TASK_COLUMN = 'title' as const;

/**
 * El conjunto por defecto — exactamente lo que la tabla pinta hoy, menos la casilla.
 *
 * 🚨 Es lo que se usa cuando una vista NO tiene columnas guardadas, que es el caso de todas las
 * vistas creadas antes de #1099. Ausencia significa «las de siempre», nunca «ninguna».
 */
export const DEFAULT_TASK_COLUMNS = [
  'title',
  'status',
  'priority',
  'assignee',
  'client',
  'dueDate',
  'createdAt',
] as const;

export const TableSortFieldEnum = z.enum([
  'title',
  'priority',
  'dueDate',
  'startDate',
  'createdAt',
  'updatedAt',
  'position',
  'estimate',
]);

/**
 * Validation for sort direction.
 */
export const SortDirectionEnum = z.enum(['asc', 'desc']);

// ─── Board View Query ───

/**
 * Validation schema for Board (Kanban) view query parameters.
 * Used on GET /api/v1/tasks?view=board&listId={id}
 *
 * Board view fetches ALL tasks for a list (no cursor pagination)
 * because the board must render all columns simultaneously.
 * @example
 * const query = BoardViewQuerySchema.parse({
 *   view: 'board',
 *   listId: 'list-uuid',
 * });
 */
export const BoardViewQuerySchema = z.object({
  /** Must be 'board' to activate board view */
  view: z.literal('board'),
  /** ID of the list to display in board view */
  listId: z.string().uuid('listId must be a valid UUID'),
});

/** Type inferred from BoardViewQuerySchema */
export type BoardViewQueryInput = z.infer<typeof BoardViewQuerySchema>;

// ─── Table View Query ───

const PriorityEnum = z.enum([
  Priority.URGENT,
  Priority.HIGH,
  Priority.NORMAL,
  Priority.LOW,
  Priority.NONE,
]);

/**
 * Validation schema for Table (spreadsheet) view query parameters.
 * Used on GET /api/v1/tasks?view=table&listId={id}
 *
 * Table view supports sorting, filtering, and cursor pagination.
 * @example
 * const query = TableViewQuerySchema.parse({
 *   view: 'table',
 *   listId: 'list-uuid',
 *   sortBy: 'dueDate',
 *   sortDir: 'asc',
 *   priorities: ['HIGH', 'URGENT'],
 *   limit: 25,
 * });
 */
export const TableViewQuerySchema = z.object({
  /** Must be 'table' to activate table view */
  view: z.literal('table'),
  /** ID of the list to display in table view */
  listId: z.string().uuid('listId must be a valid UUID'),
  /** Which field to sort by (defaults to position) */
  sortBy: TableSortFieldEnum.default('position'),
  /** Sort direction (defaults to ascending) */
  sortDir: SortDirectionEnum.default('asc'),
  /** Filter by priority levels (multiple allowed) */
  priorities: z
    .union([z.array(PriorityEnum), PriorityEnum.transform((v) => [v])])
    .optional()
    .default([]),
  /** Filter by assigned users (multiple allowed) */
  assigneeIds: z
    .union([
      z.array(z.string().uuid()),
      z
        .string()
        .uuid()
        .transform((v) => [v]),
    ])
    .optional()
    .default([]),
  /** Filter by due date start (ISO 8601) */
  dueDateFrom: z.string().datetime().optional(),
  /** Filter by due date end (ISO 8601) */
  dueDateTo: z.string().datetime().optional(),
  /** Search query (matches title, description) */
  search: z
    .string()
    .max(200)
    .optional()
    .transform((v) => (v?.trim() === '' ? undefined : v?.trim())),
  /** Cursor for pagination */
  cursor: z.string().optional(),
  /** Items per page (defaults to 50) */
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

/** Type inferred from TableViewQuerySchema */
export type TableViewQueryInput = z.infer<typeof TableViewQuerySchema>;

// ─── Unified View Query (discriminated union) ───

/**
 * Discriminated union for all view-specific query schemas.
 * The `view` field determines which schema branch validates the query.
 * Falls back to the default list query when no view is specified.
 * @example
 * const query = ViewQuerySchema.parse({
 *   view: 'board',
 *   listId: 'list-uuid',
 * });
 */
export const ViewQuerySchema = z.discriminatedUnion('view', [
  BoardViewQuerySchema,
  TableViewQuerySchema,
]);

/** Type inferred from ViewQuerySchema (discriminated union) */
export type ViewQueryInput = z.infer<typeof ViewQuerySchema>;

// ─── Bulk Position Update ───

/**
 * Schema for a single position update within a bulk operation.
 * Part of the request body for PATCH /api/v1/tasks/bulk/positions.
 */
export const BulkPositionUpdateItemSchema = z.object({
  /** ID of the task being repositioned */
  taskId: z.string().uuid('taskId must be a valid UUID'),
  /** New status (column) for the task */
  statusId: z.string().uuid('statusId must be a valid UUID'),
  /** New position within the status column */
  position: z.number().int().nonnegative('position must be a non-negative integer'),
});

/** Type inferred from BulkPositionUpdateItemSchema */
export type BulkPositionUpdateItemInput = z.infer<typeof BulkPositionUpdateItemSchema>;

/**
 * Validation schema for bulk position update request body.
 * Used on PATCH /api/v1/tasks/bulk/positions.
 * Sent after a drag-and-drop operation that affects multiple tasks.
 *
 * Constraints:
 * - listId scopes all updates to a single list (security boundary)
 * - updates array must have 1-100 items (prevent unbounded writes)
 * - Each taskId must be unique within the request (prevent conflicting updates)
 *
 * @example
 * const input = BulkPositionUpdateSchema.parse({
 *   listId: 'list-uuid',
 *   updates: [
 *     { taskId: 'task-1', statusId: 'status-done', position: 0 },
 *     { taskId: 'task-2', statusId: 'status-in-progress', position: 5 },
 *   ],
 * });
 */
export const BulkPositionUpdateSchema = z
  .object({
    /** ID of the list containing all tasks being updated (required) */
    listId: z.string().uuid('listId must be a valid UUID'),
    /** Array of position updates (1-100 items) */
    // Stable message KEYS, not prose (Batch B7, docs/phase9/batch-b-i18n.md §2 Pattern A).
    updates: z
      .array(BulkPositionUpdateItemSchema)
      .min(1, 'views.positions.atLeastOne')
      .max(100, 'views.positions.tooMany'),
  })
  .refine(
    (data) => {
      const taskIds = data.updates.map((u) => u.taskId);
      return new Set(taskIds).size === taskIds.length;
    },
    {
      message: 'views.positions.duplicateTaskId',
      path: ['updates'],
    },
  );

/** Type inferred from BulkPositionUpdateSchema */
export type BulkPositionUpdateInput = z.infer<typeof BulkPositionUpdateSchema>;

// ─── Table Column Config ───

/**
 * Validation schema for user-specific table column preferences.
 * Tracks which columns are visible and their widths in the table view.
 * @example
 * const config = TableColumnConfigSchema.parse({
 *   field: 'title',
 *   visible: true,
 *   width: 300,
 * });
 */
export const TableColumnConfigSchema = z.object({
  /** Column field name (e.g., 'title', 'dueDate', 'priority') */
  field: z.string().min(1).max(50),
  /** Whether this column is visible in the table */
  visible: z.boolean(),
  /** Column width in pixels (50-1000, null = auto) */
  width: z.number().int().min(50).max(1000).nullable(),
});

/** Type inferred from TableColumnConfigSchema */
export type TableColumnConfigInput = z.infer<typeof TableColumnConfigSchema>;
